import React, { useState, Suspense, lazy } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Star, MapPin, Camera, Clock } from "lucide-react";
import { useServices } from "@/hooks/useServices";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
const ServiceLocationMap = lazy(() => import("@/components/ServiceLocationMap").then(m => ({ default: m.ServiceLocationMap })));
import { ServiceImageUpload } from "@/components/services/ServiceImageUpload";
import { AvailabilitySchedule, getDefaultSchedule, WeeklySchedule } from "@/components/services/AvailabilitySchedule";
import { CollaboratorPicker, Collaborator } from "@/components/services/CollaboratorPicker";
import { useServiceCollaborators } from "@/hooks/useServiceCollaborators";

const serviceSchema = z.object({
  title: z.string().min(5, "Title must be at least 5 characters"),
  description: z.string().min(20, "Description must be at least 20 characters"),
  category_id: z.string().uuid("Please select a category"),
  price: z.coerce.number().min(5, "Price must be at least $5"),
  expertiseLevel: z.enum(["beginner", "intermediate", "expert"]),
  delivery_method: z.enum(["virtual", "in-person", "both"], {
    required_error: "Please select a delivery method",
  }),
  service_location: z.object({
    latitude: z.number(),
    longitude: z.number(),
    radius: z.number().min(1, "Radius must be at least 1 km"),
  }).optional(),
  image_url: z.string().url().optional().or(z.literal("")),
}).refine((data) => {
  // If delivery method requires location, ensure location is provided
  if (data.delivery_method === "in-person" || data.delivery_method === "both") {
    return data.service_location !== undefined;
  }
  return true;
}, {
  message: "Service location is required for in-person or hybrid delivery methods",
  path: ["service_location"]
});

type ServiceFormValues = z.infer<typeof serviceSchema>;

const OfferSkills = () => {
  const { user } = useAuth();
  const { categories, isCategoriesLoading, createServiceAsync } = useServices();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceLocation, setServiceLocation] = useState<{latitude: number, longitude: number, radius: number} | null>(null);
  const [currentDeliveryMethod, setCurrentDeliveryMethod] = useState<"virtual" | "in-person" | "both">("virtual");
  const [serviceImageUrl, setServiceImageUrl] = useState<string | null>(null);
  const [availabilitySchedule, setAvailabilitySchedule] = useState<WeeklySchedule>(getDefaultSchedule());
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const { saveCollaborators } = useServiceCollaborators();
  const navigate = useNavigate();
  
  const form = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      title: "",
      description: "",
      price: 0,
      expertiseLevel: "beginner",
      delivery_method: "virtual",
      image_url: "",
    },
  });

  const requiresLocation = currentDeliveryMethod === "in-person" || currentDeliveryMethod === "both";

  const expertiseLevels = [
    { value: "beginner", label: "Beginner" },
    { value: "intermediate", label: "Intermediate" },
    { value: "expert", label: "Expert" },
  ];

  const handleDeliveryMethodChange = (value: "virtual" | "in-person" | "both") => {
    console.log("Delivery method changed to:", value);
    setCurrentDeliveryMethod(value);
    form.setValue("delivery_method", value);
    
    // Clear location data if switching to virtual
    if (value === "virtual") {
      setServiceLocation(null);
      form.setValue("service_location", undefined);
    }
    
    // Clear any previous validation errors
    form.clearErrors("service_location");
  };

  const handleLocationChange = (location: {latitude: number, longitude: number, radius: number}) => {
    console.log("Location changed:", location);
    setServiceLocation(location);
    form.setValue("service_location", location);
    form.clearErrors("service_location");
  };

  const handleImageChange = (imageUrl: string | null) => {
    console.log("Image changed:", imageUrl);
    setServiceImageUrl(imageUrl);
    form.setValue("image_url", imageUrl || "");
  };

  const onSubmit = async (data: ServiceFormValues) => {
    if (!user) {
      toast.error("You must be logged in to submit a service");
      navigate("/auth");
      return;
    }

    // Additional validation for location requirement
    if (requiresLocation && !serviceLocation) {
      toast.error("Please set a service location on the map for in-person or hybrid services");
      form.setError("service_location", {
        type: "required",
        message: "Service location is required for in-person or hybrid delivery methods"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("[FORM SUBMIT] Starting with data:", data);
      
      // Validate collaborator percentages
      const totalCollabPercentage = collaborators.reduce((sum, c) => sum + c.percentage, 0);
      if (totalCollabPercentage >= 100) {
        toast.error("Total collaborator percentages must be less than 100%");
        return;
      }
      
      // Prepare service data - ensure all fields are explicitly included
      // Only include schedule if at least one day is enabled
      const hasSchedule = requiresLocation && Object.values(availabilitySchedule).some(day => day.enabled);
      
      const serviceData = {
        title: data.title,
        description: data.description,
        category_id: data.category_id,
        price: data.price,
        pricing_type: "fixed" as const,
        delivery_method: data.delivery_method as "virtual" | "in-person" | "both",
        service_location: requiresLocation ? serviceLocation : null,
        availability_schedule: hasSchedule ? availabilitySchedule : null,
        image_url: serviceImageUrl || null,
      };
      
      console.log("[FORM SUBMIT] Prepared service data:", serviceData);
      
      // Create the service
      const createdService = await createServiceAsync(serviceData);
      console.log("[FORM SUBMIT] Service creation mutation completed", createdService);
      
      // Save collaborators if any
      if (collaborators.length > 0 && createdService?.id) {
        try {
          await saveCollaborators(createdService.id, collaborators);
          console.log("[FORM SUBMIT] Collaborators saved successfully");
        } catch (collabError) {
          console.error("[FORM SUBMIT] Error saving collaborators:", collabError);
          toast.error("Service created but failed to save collaborators");
        }
      }
      
      // If no error is thrown, we can navigate
      navigate("/discover-services");
    } catch (error: any) {
      console.error("[FORM SUBMIT] Error:", error);
      toast.error(error.message || "Failed to create service");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-poppins font-bold text-3xl md:text-4xl mb-4 text-anyskill-purple dark:text-anyskill-purple">
            Offer a Service
          </h1>
          <p className="font-inter text-lg text-muted-foreground mb-8">
            Share your talents with the world and start earning. Fill out the form below to create your service listing.
          </p>

          {!user ? (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 mb-8">
              <h2 className="font-semibold text-lg mb-2 text-amber-700 dark:text-amber-400">Login Required</h2>
              <p className="mb-4 text-amber-700 dark:text-amber-400">You need to be logged in to offer a service.</p>
              <Button 
                onClick={() => navigate("/auth")}
                className="bg-anyskill-purple hover:bg-anyskill-purple/90"
              >
                Go to Login
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                {/* Service Details Section */}
                <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                  <h2 className="font-poppins font-semibold text-xl mb-4 flex items-center text-foreground">
                    <Star className="mr-2 text-anyskill-blue" />
                    Service Details
                  </h2>
                  
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-medium">Service Title</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="E.g., Professional Logo Design, Math Tutoring, Web Development" 
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-medium">Service Description</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Describe what you offer, your experience, and what clients can expect from your service..." 
                              className="h-32"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="category_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-medium">Category</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            value={field.value}
                            disabled={isCategoriesLoading}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a category" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectGroup>
                                {categories?.map((category) => (
                                  <SelectItem key={category.id} value={category.id}>
                                    {category.name}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="expertiseLevel"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-medium">Expertise Level</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select your expertise level" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectGroup>
                                {expertiseLevels.map((level) => (
                                  <SelectItem key={level.value} value={level.value}>
                                    {level.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-medium">Price (USD)</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              placeholder="Enter your price" 
                              {...field}
                            />
                          </FormControl>
                          <p className="text-sm text-muted-foreground mt-1">
                            You can adjust the price later in conversations with clients.
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="delivery_method"
                      render={({ field }) => (
                        <FormItem className="space-y-3">
                          <FormLabel className="font-medium">Delivery Method</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={(value) => {
                                field.onChange(value);
                                handleDeliveryMethodChange(value as "virtual" | "in-person" | "both");
                              }}
                              value={currentDeliveryMethod}
                              className="flex flex-col space-y-1"
                            >
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="virtual" />
                                </FormControl>
                                <FormLabel className="font-normal cursor-pointer">
                                  Virtual (online only)
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="in-person" />
                                </FormControl>
                                <FormLabel className="font-normal cursor-pointer">
                                  In-Person (face to face)
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="both" />
                                </FormControl>
                                <FormLabel className="font-normal cursor-pointer">
                                  Both (virtual and in-person available)
                                </FormLabel>
                              </FormItem>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Service Image Section - Make it more prominent */}
                <div className="bg-card rounded-lg shadow-md p-6 border-l-4 border-anyskill-blue border border-border">
                  <h2 className="font-poppins font-semibold text-xl mb-4 flex items-center text-foreground">
                    <Camera className="mr-2 text-anyskill-blue" />
                    Service Image
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add an image to make your service more appealing to potential clients. This helps your service stand out and gives clients a better sense of what you offer.
                  </p>
                  
                  <ServiceImageUpload 
                    onImageChange={handleImageChange}
                    currentImageUrl={serviceImageUrl}
                  />
                </div>

                {/* Collaborators Section */}
                <CollaboratorPicker
                  collaborators={collaborators}
                  onCollaboratorsChange={setCollaborators}
                />
                {requiresLocation && (
                  <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                    <h2 className="font-poppins font-semibold text-xl mb-4 flex items-center text-foreground">
                      <MapPin className="mr-2 text-anyskill-blue" />
                      Service Location
                    </h2>
                    <p className="text-sm text-muted-foreground mb-4">
                      Set your service area by clicking on the map to place a marker and adjusting the radius.
                    </p>
                    <Suspense fallback={null}>
                      <ServiceLocationMap onLocationChange={handleLocationChange} />
                    </Suspense>
                    {form.formState.errors.service_location && (
                      <p className="text-sm font-medium text-destructive mt-2">
                        {form.formState.errors.service_location.message}
                      </p>
                    )}
                  </div>
                )}

                {/* Availability Schedule Section - Only show for in-person or both */}
                {requiresLocation && (
                  <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                    <h2 className="font-poppins font-semibold text-xl mb-4 flex items-center text-foreground">
                      <Clock className="mr-2 text-anyskill-blue" />
                      Availability Schedule
                    </h2>
                    <p className="text-sm text-muted-foreground mb-4">
                      Set your available hours for in-person services. Toggle on the days you're available and set your start and end times.
                    </p>
                    <AvailabilitySchedule
                      schedule={availabilitySchedule}
                      onChange={setAvailabilitySchedule}
                    />
                  </div>
                )}
                
                <div className="flex justify-end">
                  <Button 
                    type="submit" 
                    className="bg-anyskill-purple hover:bg-anyskill-purple/90 text-white px-8 py-6 font-inter font-medium text-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Service Offering"}
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferSkills;
